import React from 'react';
import { CheckOutlined, ClockCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, LoadingOutlined, MinusCircleOutlined, StopOutlined } from '@ant-design/icons';


import {
  isThinkingBlocked,
  isThinkingCancelled,
  isThinkingCompleted,
  isThinkingFailed,
  isThinkingPartial,
  isThinkingWaitingUser,
  resolveThinkingStage,
} from './thinkingState.js';

import { resolveProcessSummary } from './thinkingProcessPresentation.js';
import { hasAuthoritativeWorkflowState, workflowProgressProjection, workflowRunState } from './workflowRunProjectionState.js';
import { resolveThinkingProcessEntries } from './thinkingProcessLines.js';


const THINKING_STATUS_LABELS = new Map([
  ['conflict', '完成情况待核实'],
  ['unavailable', '完成情况待核实'],
  ['pending', '待执行'],
  ['failed', '失败'],
  ['blocked', '已阻塞'],
  ['cancelled', '已取消'],
  ['waiting', '等待确认'],
  ['partial', '部分完成'],
  ['completed', '已完成'],
  ['running', '运行中'],
]);
const TRACKER_STATUS_KEYS = new Map([
  ['planned', 'pending'],
  ['waiting_user', 'waiting'],
]);
const TRACKER_ITEM_STATUS_KEYS = new Map([
  ['not_started', 'pending'],
  ['ready', 'pending'],
]);
const TRACK_RESULT_ITEM_STATUS_KEYS = new Map([
  ['active', 'running'],
]);

const conditionalValue = (condition, whenTrue, whenFalse) => (
  condition ? whenTrue : whenFalse
);

export function resolveStatusStyleKey(status = '') {
  return status;
}

function firstTruthy(...values) {
  return values.find(Boolean);
}

function resolveWorkflowTracker(item = {}) {
  const tracker = item?.workflowTracker;
  return tracker?.source === 'backend_canonical' ? tracker : null;
}

function resolveWorkflowTrackerEntries(tracker = null) {
  const items = Array.isArray(tracker?.items) ? tracker.items : [];
  return items
    .map((entry) => {
      const record = Object.assign({}, entry);
      const state = String(record.state || '').trim();
      return {
        label: String(record.label || '').trim(),
        state: TRACKER_ITEM_STATUS_KEYS.get(state) ?? state,
        stepId: String(record.item_id || '').trim(),
      };
    })
    .filter(({ label }) => Boolean(label))
    .map(({ label, state, stepId }) => ({
      line: `[${state || 'pending'}] ${label}`,
      scope: 'business',
      stepId,
    }));
}

function resolveTrackResultEntries(trackResult = null) {
  const steps = Array.isArray(trackResult?.steps)
    ? trackResult.steps
    : (Array.isArray(trackResult?.workflow_steps) ? trackResult.workflow_steps : []);
  return steps
    .map((step) => {
      const record = Object.assign({}, step);
      const state = String(record.status || '').trim();
      return {
        label: String(firstTruthy(record.title, record.label, '')).trim(),
        state: TRACK_RESULT_ITEM_STATUS_KEYS.get(state) ?? state,
        stepId: String(record.step_id || '').trim(),
      };
    })
    .filter(({ label }) => Boolean(label))
    .map(({ label, state, stepId }) => ({
      line: `[${state || 'pending'}] ${label}`,
      scope: 'business',
      stepId,
    }));
}

export function shouldOpenThinkingProcess({
  completed = false,
  hasProcessLines = false,
  waitingUser = false,
} = {}) {
  return hasProcessLines && (!completed || waitingUser);
}

export function shouldAnimateThinkingProcess(input) {
  const {
    hasProcessLines, historical, completed, waitingUser, blocked, cancelled, partial, authoritative,
  } = Object.assign({
    blocked: false, cancelled: false, completed: false, hasProcessLines: false,
    historical: false, partial: false, waitingUser: false, authoritative: false,
  }, input);
  return [
    !authoritative,
    shouldOpenThinkingProcess({
      hasProcessLines,
      completed,
      blocked,
      cancelled,
      partial,
    }),
    !waitingUser,
    !historical,
    !completed,
  ].every(Boolean);
}

const resolveThinkingStatusKey = ({ blocked, cancelled, failed, isSettled, partial, waitingUser, authoritativeStatus }) => (
  ({ waiting_user: 'waiting', degraded: 'partial' })[authoritativeStatus] || authoritativeStatus ||
  ([
    [failed, 'failed'],
    [blocked, 'blocked'],
    [cancelled, 'cancelled'],
    [waitingUser, 'waiting'],
    [partial, 'partial'],
    [isSettled, 'completed'],
  ].find(([matches]) => matches)?.[1] ?? 'running')
);

const resolveThinkingStatusIcon = (statusKey) => {
  const icons = new Map([
    ['running', <LoadingOutlined spin className="flare-thinking-status-icon" />],
    ['conflict', <MinusCircleOutlined className="flare-thinking-status-icon" />],
    ['unavailable', <MinusCircleOutlined className="flare-thinking-status-icon" />],
    ['failed', <CloseCircleOutlined className="flare-thinking-status-icon" />],
    ['waiting', <ClockCircleOutlined className="flare-thinking-status-icon" />],
    ['partial', <ExclamationCircleOutlined className="flare-thinking-status-icon" />],
    ['blocked', <StopOutlined className="flare-thinking-status-icon" />],
    ['cancelled', <StopOutlined className="flare-thinking-status-icon" />],
  ]);
  return icons.get(statusKey) ?? <CheckOutlined className="flare-thinking-status-icon" />;
};

export function resolveWorkflowState(item, hasStreamingContent) {
  const workflowTracker = resolveWorkflowTracker(item);
  const trackerProcessEntries = resolveWorkflowTrackerEntries(workflowTracker);
  const hasWorkflowTracker = trackerProcessEntries.length > 0;
  const trackResult = Object.assign({}, item).trackResult;
  const trackResultProcessEntries = resolveTrackResultEntries(trackResult);
  const hasTrackResult = trackResultProcessEntries.length > 0;
  const authoritative = [
    hasAuthoritativeWorkflowState(item),
    hasWorkflowTracker,
    hasTrackResult,
  ].some(Boolean);
  const progress = workflowProgressProjection(item);
  const completionEvidence = Object.assign({}, Object.assign({}, progress).completion_evidence);
  const completionEvidenceStatus = completionEvidence.status;
  const completionStatusConflict = completionEvidenceStatus === 'conflict';
  const settlementVisible = [authoritative, !hasStreamingContent].some(Boolean);
  const trackerObjective = Object.assign({}, Object.assign({}, workflowTracker).objective);
  const trackerObjectiveLabel = String(trackerObjective.label || '').trim();
  const trackResultTitle = String(Object.assign({}, trackResult).title || '').trim();
  return {
    authoritative,
    completionEvidenceStatus,
    completionEvidenceUncertain: ['conflict', 'unavailable'].includes(completionEvidenceStatus),
    completionStatusConflict,
    hasBusinessProgress: [hasWorkflowTracker, hasTrackResult].some(Boolean),
    hasTrackResult,
    hasWorkflowTracker,
    progress,
    settlementVisible,
    stageLabel: conditionalValue(
      completionStatusConflict,
      '',
      firstTruthy(trackerObjectiveLabel, trackResultTitle, resolveThinkingStage(item, hasStreamingContent)),
    ),
    trackResult,
    trackResultProcessEntries,
    trackerProcessEntries,
    workflowTracker,
  };
}

export function resolveSettlementState(item, settlementVisible) {
  return {
    blocked: [isThinkingBlocked(item), settlementVisible].every(Boolean),
    cancelled: [isThinkingCancelled(item), settlementVisible].every(Boolean),
    completed: [isThinkingCompleted(item), settlementVisible].every(Boolean),
    failed: isThinkingFailed(item),
    partial: [isThinkingPartial(item), settlementVisible].every(Boolean),
    waitingUser: [isThinkingWaitingUser(item), settlementVisible].every(Boolean),
  };
}

export function resolveProcessScope({
  item,
  hasWorkflowTracker,
  trackerProcessEntries,
  hasTrackResult,
  trackResultProcessEntries,
}) {
  const authoritativeEntries = [
    [hasWorkflowTracker, trackerProcessEntries],
    [hasTrackResult, trackResultProcessEntries],
  ].find(([matches]) => matches);
  return authoritativeEntries ? authoritativeEntries[1] : resolveThinkingProcessEntries(item);
}

export function resolveBusinessProcessSummary({
  hasBusinessProgress,
  hasWorkflowTracker,
  hasTrackResult,
  processLines,
  progress,
  workflowTracker,
  trackResult,
}) {
  if ([!hasBusinessProgress, hasWorkflowTracker, hasTrackResult].some(Boolean)) {
    return firstTruthy(workflowTracker?.objective?.label, trackResult?.title, '');
  }
  const progressRecord = Object.assign({}, progress);
  return resolveProcessSummary(
    processLines.map((line) => String(line || '').split('\n')[0]),
    progress === null ? null : Object.assign({}, progressRecord.progress),
  );
}

export function resolveStatusPresentation({
  item,
  workflowTracker,
  workflowState,
  settlement,
  tool,
  historical,
  hasStreamingContent,
}) {
  const {
    authoritative, completionEvidenceStatus, completionStatusConflict, stageLabel,
  } = workflowState;
  const {
    blocked, cancelled, completed, failed, partial, waitingUser,
  } = settlement;
  const hasSecondaryLabel = [
    !failed,
    !completed,
    !waitingUser,
    !partial,
    tool.label,
    tool.label !== stageLabel,
  ].every(Boolean);
  const dimmed = [historical, hasStreamingContent, waitingUser, blocked, cancelled, failed, partial]
    .some(Boolean);
  const isSettled = [
    [historical, !authoritative].every(Boolean),
    completed,
    waitingUser,
    blocked,
    cancelled,
    failed,
    partial,
  ].some(Boolean);
  const trackerStatus = String(Object.assign({}, workflowTracker).status || '').trim();
  const trackerStatusKey = TRACKER_STATUS_KEYS.get(trackerStatus) ?? trackerStatus;
  const statusKey = resolveThinkingStatusKey({
    blocked,
    cancelled,
    failed,
    isSettled,
    partial,
    waitingUser,
    authoritativeStatus: conditionalValue(
      completionStatusConflict,
      completionEvidenceStatus,
      firstTruthy(trackerStatusKey, workflowRunState(item)),
    ),
  });
  return {
    canRegenerate: failed,
    dimmed,
    isSettled,
    secondaryLabel: conditionalValue(hasSecondaryLabel, tool.label, ''),
    statusIcon: resolveThinkingStatusIcon(statusKey),
    statusKey,
    statusLabel: THINKING_STATUS_LABELS.get(statusKey),
  };
}
